import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
  Stack,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useResponsive from "../hooks/useResponsive";
import Logo from "../components/Logo";
import { VerifyPasswordChanging } from "../AuthenticationData.ts";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3, 4),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(8, 3, 2, 3),
  overflowY: "auto",
  [theme.breakpoints.up("md")]: {
    width: "35%",
    alignItems: "center",
    padding: theme.spacing(3, 4),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 5),
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const PasswordStrengthBar = ({ strength }) => {
  const getStrengthProps = () => {
    switch (strength) {
      case "weak":
        return { color: "error", value: 33, label: "Weak" };
      case "medium":
        return { color: "warning", value: 66, label: "Medium" };
      case "strong":
        return { color: "success", value: 100, label: "Strong" };
      default:
        return { value: 0 };
    }
  };
  const props = getStrengthProps();
  if (props.value === 0) return null;

  return (
    <Box>
      <LinearProgress
        variant="determinate"
        sx={{ height: 6, borderRadius: 1 }}
        {...props}
      />
      <Typography
        variant="caption"
        color={`${props.color}.main`}
        sx={{ display: "block", textAlign: "right", mt: 0.5 }}
      >
        {props.label}
      </Typography>
    </Box>
  );
};

export default function VerifyPasswordChangingPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const mdUp = useResponsive("up", "md");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [strength, setStrength] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    validatePassword(password);
  }, [password, confirmPassword]);

  const validatePassword = (pwd) => {
    if (!pwd) {
      setPasswordError("");
      setStrength("");
      return;
    }

    let strengthIndicator = -1;
    const checks = {
      upper: /[A-Z]/.test(pwd),
      lower: /[a-z]/.test(pwd),
      numbers: /\d/.test(pwd),
      specialChars: /[!@#$%^&*()_+\-=\]{};':"\\|,.<>\/?]/.test(pwd),
    };

    if (pwd.length >= 8) {
      strengthIndicator++;
      if (pwd.length > 10) strengthIndicator++;
    }
    if (checks.upper && checks.lower && checks.numbers && checks.specialChars) {
      strengthIndicator++;
    }
    setStrength(["weak", "medium", "strong"][strengthIndicator] || "");

    let errorMsg = "";
    if (!pwd) {
    } else if (pwd.length < 8) {
      errorMsg = "Password must be at least 8 characters long.";
    } else if (!checks.specialChars) {
      errorMsg = "Password must contain a special character.";
    } else if (!checks.upper) {
      errorMsg = "Password must contain an uppercase letter.";
    } else if (!checks.lower) {
      errorMsg = "Password must contain a lowercase letter.";
    } else if (!checks.numbers) {
      errorMsg = "Password must contain a number.";
    } else if (confirmPassword && pwd !== confirmPassword) {
      errorMsg = "Passwords do not match.";
    }
    setPasswordError(errorMsg);
  };

  const handleConfirm = async () => {
    if (!password || !confirmPassword) {
      toast.error("Please fill in both password fields.", {
        position: "top-center",
      });
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.", { position: "top-center" });
      return;
    }
    if (passwordError) {
      toast.error(passwordError, { position: "top-center" });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await VerifyPasswordChanging(confirmPassword, token);
      if (response && response.status === 200) {
        toast.success("Password changed successfully!", {
          position: "top-center",
        });
        setPasswordChanged(true);
      } else {
        const errorMessage =
          response?.data?.error ||
          response?.data?.message ||
          "Error changing password.";
        toast.error(errorMessage, { position: "top-center" });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("An unexpected error occurred.", { position: "top-center" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contactie | Reset Password</title>
      </Helmet>

      <StyledPageContainer>
        <ContentWrapper>
          <Box
            component="header"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              zIndex: 10,
              top: 40,
              left: 40,
            }}
          >
            <Logo sx={{ height: 40, width: "auto" }} />
          </Box>

          {mdUp && (
            <StyledSection>
              <Typography
                variant="h4"
                sx={{ textAlign: "center", mb: 2, px: 5 }}
              >
                Set a New Password
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                Your account's security is our top priority.
              </Typography>
              <Box
                component="img"
                src="/assets/illustrations/illustration_set_password.svg"
                alt="Reset Password Illustration"
                sx={{ width: "100%", maxWidth: 320, mt: 4 }}
              />
            </StyledSection>
          )}

          <StyledContent>
            <FormContainer>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Logo sx={{ height: 40, width: "auto", mb: 3 }} />
                <Typography variant="h4" gutterBottom>
                  {passwordChanged ? "Success!" : "Create Password"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3 }}
                >
                  {passwordChanged
                    ? "You can now log in with your new password."
                    : "Please enter and confirm your new password below."}
                </Typography>
                <Box
                  component="img"
                  src={
                    passwordChanged
                      ? "/assets/illustrations/illustration_set_password.svg"
                      : "/assets/illustrations/illustration_set_password.svg"
                  }
                  alt="status illustration"
                  sx={{ height: 180 }}
                />
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" }, mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  {passwordChanged
                    ? "Password Updated Successfully"
                    : "Create a new password"}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {passwordChanged
                    ? "Your account is now secure. You may return to the login page."
                    : "Your new password must be strong and different from previous passwords."}
                </Typography>
              </Box>

              {passwordChanged ? (
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={() => navigate("/login")}
                >
                  Return to Login
                </Button>
              ) : (
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText={passwordError && password ? passwordError : " "}
                    error={!!passwordError && !!password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <PasswordStrengthBar strength={strength} />

                  <TextField
                    fullWidth
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!confirmPassword && password !== confirmPassword}
                    helperText={
                      !!confirmPassword && password !== confirmPassword
                        ? "Passwords do not match."
                        : " "
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={
                      isSubmitting ||
                      !!passwordError ||
                      !password ||
                      !confirmPassword ||
                      strength !== "strong"
                    }
                  >
                    {isSubmitting ? "Updating..." : "Update Password"}
                  </Button>
                </Stack>
              )}
            </FormContainer>
          </StyledContent>
        </ContentWrapper>

        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
